
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import IconAddPcBg from "~/components/v2/icons/IconAddPcBg.vue";
import IconAddMobileBg from "~/components/v2/icons/IconAddMobileBg.vue";
import IconAddBg from "~/components/v2/icons/IconAddBg.vue";
import { mdiClose } from "@mdi/js";

export default {
  components: {
    cropper: Cropper,
    IconAddPcBg,
    IconAddMobileBg,
    IconAddBg,
  },

  props: {
    profileId: {
      type: Number,
      default: 0,
    },
    deviceType: {
      type: String,
      default: "pc", // pc or mobile
    },
    url: {
      type: String,
      default: null,
    },
    urlFromGrid: {
      type: String,
      default: null,
    },
    approved: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close", "changed"],

  data() {
    return {
      mdiClose,

      isOpen: false,
      file: null,
      fileWidth: null,
      fileHeight: null,

      loading: false,
      cropData: {},
      changePhotoUrl: "/api/profiles/bg/change",
      imgRestrictions: {
        minWidth: 500,
        minHeight: 500,
        maxSize: 10 * 1024 * 1024,
      },
    };
  },

  computed: {
    stencilProps() {
      if (this.deviceType === "pc") return { aspectRatio: 16 / 9 };
      return { aspectRatio: 9 / 16 };
    },

    cropperDimensions() {
      if (this.deviceType === "pc") return { width: 500 };
      return { height: 500 };
    },

    previewImgUrl() {
      if (this.file) {
        return URL.createObjectURL(this.file);
      }
      if (this.url) {
        return this.url;
      }
      return null;
    },

    successMessage() {
      return this.url || this.approved
        ? this.$t("bg-photo-changed")
        : this.$t("request-for-photo-change");
    },

    cropFieldName() {
      return this.deviceType === "pc"
        ? "desktop_crop_data"
        : "mobile_crop_data";
    },

    pcHeader() {
      return this.$device.isMobile
        ? this.$t("upload-pc-bg-short")
        : this.$t("upload-pc-bg");
    },

    mobileHeader() {
      return this.$device.isMobile
        ? this.$t("upload-mobile-bg-short")
        : this.$t("upload-mobile-bg");
    },

    modalHeader() {
      return this.deviceType === "pc" ? this.pcHeader : this.mobileHeader;
    },

    uploadIcon() {
      return this.deviceType === "pc" ? "IconAddPcBg" : "IconAddMobileBg";
    },

    dialogProps() {
      const props = {
        attach: "#app",
      };

      if (
        this.$device.isMobile ||
        (typeof window !== "undefined" && window.innerWidth < 710)
      ) {
        props.fullscreen = true;
        props.transition = "dialog-bottom-transition";
        props.contentClass = "upload-background-modal__dialog";
      } else {
        props.maxWidth = "860";
      }

      return props;
    },
  },

  methods: {
    changeCropperData({ coordinates }) {
      for (const key in coordinates) {
        const element = coordinates[key];
        this.cropData[key] = Math.round(element);
      }
    },

    onFileChange(e) {
      const file = e.target.files[0];
      let isValid = false;

      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        this.fileWidth = img.width;
        this.fileHeight = img.height;

        if (
          this.fileWidth > this.imgRestrictions.minWidth &&
          this.fileHeight > this.imgRestrictions.minHeight
        ) {
          isValid = true;
        } else {
          this.$notify({
            type: "warning",
            message: this.$t("validations.photo-min-dimensions", [
              file.name,
              this.imgRestrictions.minWidth,
              this.imgRestrictions.minHeight,
            ]),
          });
        }

        URL.revokeObjectURL(img.src);

        if (file.size <= this.imgRestrictions.maxSize) {
          isValid = isValid && true;
        } else {
          isValid = false;
          this.$notify({
            type: "warning",
            message: this.$t("validations.photo-max-size", [
              file.name,
              this.imgRestrictions.maxSize / 1024 / 1024,
            ]),
          });
        }

        if (isValid) {
          this.file = file;
        }
      };
    },

    clickInput() {
      this.$refs.fileInput.click();
    },

    open() {
      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
      this.file = null;
      this.fileWidth = null;
      this.fileHeight = null;

      this.loading = false;
      this.cropData = {};
      this.$emit("close");
    },

    uploadPhoto() {
      const formData = new FormData();
      formData.append("photo", this.file);
      formData.append("photo_for", "background");
      formData.append("profile_id", this.profileId);
      formData.append(this.cropFieldName, JSON.stringify(this.cropData));
      return this.$axios.post("/api/profiles/photo/add", formData);
    },

    uploadUrlPhoto() {
      const formData = new FormData();
      formData.append("file_name", this.url);
      formData.append("photo_for", "background");
      formData.append("profile_id", this.profileId);
      formData.append(this.cropFieldName, JSON.stringify(this.cropData));
      return this.$axios.post("/api/profiles/photo/add", formData);
    },

    changePhoto(newPhotoUrl) {
      const params = {
        profile_id: this.profileId,
        photo: newPhotoUrl,
      };

      if (this.deviceType === "mobile") {
        params.is_mobile = true;
      }

      return this.$axios.post(this.changePhotoUrl, params);
    },

    updateUiAfterChange(newPhotoUrl) {
      this.$notify({
        type: "success",
        message: this.successMessage,
      });

      this.$store.commit(
        "profiles/setCurrentProfileForAdvertiserBg",
        newPhotoUrl
      );
    },

    async save() {
      try {
        if (this.file || this.url) {
          this.loading = true;

          if (this.file) {
            const addPcResponse = await this.uploadPhoto();

            if (addPcResponse?.data?.status) {
              this.$axios.post("/api/profiles/photo/notify", {
                profile_id: this.profileId,
              });

              const newPhotoUrl = addPcResponse.data.file;
              const changeResponse = await this.changePhoto(newPhotoUrl);

              if (changeResponse?.data?.status) {
                this.updateUiAfterChange(newPhotoUrl);
              } else {
                this.$notify({
                  type: "error",
                  message: this.$t("something-went-wrong"),
                });
              }
            } else {
              this.$notify({
                type: "error",
                message: this.$t("something-went-wrong"),
              });
            }
          }

          if (this.url) {
            const addUrlResponse = await this.uploadUrlPhoto();

            if (addUrlResponse?.data?.status) {
              this.$axios.post("/api/profiles/photo/notify", {
                profile_id: this.profileId,
              });

              const newPhotoUrl = addUrlResponse.data.file;
              const changeResponse = await this.changePhoto(newPhotoUrl);
              const changeMobileResponse = await this.changePhoto(
                newPhotoUrl,
                true
              );

              if (
                changeResponse?.data?.status &&
                changeMobileResponse?.data?.status
              ) {
                this.updateUiAfterChange(newPhotoUrl);
              } else {
                this.$notify({
                  type: "error",
                  message: this.$t("something-went-wrong"),
                });
              }
            } else {
              this.$notify({
                type: "error",
                message: this.$t("something-went-wrong"),
              });
            }
          }

          this.$emit("changed");

          if (this.urlFromGrid) {
            this.$root.$emit("use-photo-as-new", this.urlFromGrid);
          }

          this.close();
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
  },
};
